import { useEffect, useState } from 'react';
import { Headphones, Mic } from 'lucide-react';

import StorageQuotaBar from './StorageQuotaBar';
import VoicemailsPanel from './VoicemailsPanel';
import RecordingsPanel from './RecordingsPanel';
import { canReadRecordings } from '@/utilities/recordingsAcl';
import { getAssignedCallCentreId, isCallCentreVoicemailEnabled } from '@/utilities/callCentreVoicemailAcl';
import { getApiCall } from '@/service/httpCall';
import { API_CALL_CENTRE_PROFILE } from '@/service/url';
import { getUser } from '@/utilities/setting';
import { USER_ROLES } from '@/utilities/constant';

const TABS = [
  { key: 'voicemails', label: 'Voicemails', icon: Headphones },
  { key: 'recordings', label: 'Recordings', icon: Mic },
];

// Voicemails tab: the user's assigned call centre has voicemail enabled.
// Users with no call centre (superadmin, reseller, company admin…) aren't
// limited by this — for them the Recordings ACL alone decides.
function useCallCentreVoicemailEnabled() {
  const [enabled, setEnabled] = useState(() => {
    const user: any = getUser();
    return user?.role === USER_ROLES.SUPERADMIN || !getAssignedCallCentreId(user);
  });
  useEffect(() => {
    const user: any = getUser();
    if (user?.role === USER_ROLES.SUPERADMIN) return undefined;
    const ccId = getAssignedCallCentreId(user);
    if (!ccId) return undefined;
    let cancelled = false;
    getApiCall(API_CALL_CENTRE_PROFILE.replace(':id', ccId), {})
      .then((resp) => { if (!cancelled) setEnabled(isCallCentreVoicemailEnabled(resp)); })
      .catch(() => { if (!cancelled) setEnabled(false); });
    return () => { cancelled = true; };
  }, []);
  return enabled;
}

export default function CallMediaPanel() {
  const [tab, setTab] = useState('voicemails');
  // Bumped after any delete so the quota bar re-reads rather than going stale.
  const [usageKey, setUsageKey] = useState(0);
  const callCentreVoicemailEnabled = useCallCentreVoicemailEnabled();

  const onChanged = () => setUsageKey((n) => n + 1);
  // Recordings tab: Recordings `read` ACL.
  // Voicemails tab: call centre voicemail enabled + Recordings `read` ACL.
  const canRecordings = canReadRecordings();
  const tabs = TABS.filter((t) =>
    t.key === 'recordings' ? canRecordings : canRecordings && callCentreVoicemailEnabled,
  );
  const activeTab = tabs.some((t) => t.key === tab) ? tab : tabs[0]?.key;

  if (!activeTab) {
    return (
      <div className="py-6 text-center text-sm text-muted-foreground">
        You don't have access to recordings or voicemails.
      </div>
    );
  }

  return (
    <div>
      <StorageQuotaBar refreshKey={usageKey} />

      <div className="mb-3 inline-flex rounded-md border bg-card p-1" role="tablist">
        {tabs.map(({ key, label, icon: Icon }) => {
          const active = activeTab === key;
          return (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => setTab(key)}
              className={`inline-flex items-center gap-2 rounded px-3 py-1.5 text-sm font-medium transition-colors ${
                active
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon className="h-4 w-4" />
              {label}
            </button>
          );
        })}
      </div>
      {activeTab === 'voicemails' ? (
        <VoicemailsPanel onChanged={onChanged} />
      ) : (
        <RecordingsPanel onChanged={onChanged} />
      )}
    </div>
  );
}
